import { Component, ErrorInfo, ReactNode } from "react";
import { FallbackUI } from "./FallbackUI";
import { NotFoundUI } from "./NotFoundUI";
import { reportError } from "@/core/lib/datadog/datadog";
import trackPageViewEvents from "@/core/utils/trackPageViewEvents";
import { ConfigContext } from "@/core/contexts/ConfigContext";
import { InitialisationConfig } from "@/core/utils/getInitialisationConfig";

interface ErrorBoundaryProps {
  children: ReactNode;
  definitionName: string; // Add definitionName as a prop
  domain: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
  };

  static contextType = ConfigContext;
  declare context: InitialisationConfig;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    // Define a state variable to track whether is an error or not
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error({ error, errorInfo });
    reportError(error, errorInfo);
    trackPageViewEvents({
      stepTitle: "error",
      definitionName: this.props.definitionName,
      domain: this.props.domain,
    });
  }

  render() {
    const initializationConfig: InitialisationConfig = this.context;

    // Check if the error is thrown
    if (this.state.hasError || initializationConfig?.forceShowBackupCC) {
      return <FallbackUI definitionName={this.props.definitionName} />;
    }

    // Check if the process is not found
    if (initializationConfig?.notFoundProcess) {
      return <NotFoundUI />;
    }

    // Return children components in case of no error
    return this.props.children;
  }
}

export { ErrorBoundary };
